import { useMemo } from 'react';
import { getFieldFriendlyName, getDocFriendlyName, getElementTag } from '../lib/subPanelLabels';

// 04-enforce.md 4.1 -- extraído de SubPanelTratos.jsx. Responsabilidad única: derivar, a partir
// del `formData`/`documents` del trato actual y del flujo (`nodes`/`edges` + `allDocs` de todos
// los tratos del cliente), las 4 vistas consolidadas que consume el tab "Outside ins":
//   - `currentItems`      -> filas "datos de este elemento"
//   - `receivedItems`     -> filas "datos heredados de nodos upstream"
//   - `availableVariables`-> `receivedItems` deduplicado, para el autocompletado de `SmartInput`
//   - `consolidatedJson`  -> el volcado JSON `{ datos_actuales, datos_recibidos }`
// Todo es cómputo puro memoizado; no toca red ni estado. El container sólo le pasa datos ya
// cargados (los fetch siguen viviendo en el container, ver el `useEffect` de carga).

// Claves de control internas del formulario -- nunca son "datos" que se muestren o hereden.
const CONTROL_KEYS = ['public_uuid', 'is_public', 'client_submitted', 'is_verified'];

const isDisplayableValue = (val) => val != null && val !== '';

const formatValue = (val) => (typeof val === 'boolean' ? (val ? 'Sí' : 'No') : String(val));

// BFS sobre las aristas (`e.from -> e.to`) para juntar todos los ids de nodos que alcanzan
// `targetId` siguiendo las flechas hacia atrás.
const getUpstreamNodeIds = (targetId, edges) => {
  const visited = new Set();
  const queue = [targetId];
  while (queue.length > 0) {
    const current = queue.shift();
    const parents = (edges || []).filter(e => e.to === current).map(e => e.from);
    for (const parent of parents) {
      if (!visited.has(parent)) {
        visited.add(parent);
        queue.push(parent);
      }
    }
  }
  return Array.from(visited);
};

// Normaliza el `sub`/`label` de un nodo a un `document_type` canónico (el mismo que guarda la
// API en `"OperationDocument".document_type`).
const nodeToDocType = (n) => {
  const sub = (n.sub || '').toLowerCase().trim();
  const lbl = (n.label || '').toLowerCase().trim();
  if (sub.includes('form') || lbl.includes('form')) return 'formulario';
  if (sub.includes('send-terminal') || lbl.includes('send-terminal')) return 'send-terminal';
  if (sub.includes('cotiza') || lbl.includes('cotiza')) return 'cotiza';
  if (sub.includes('contrato') || lbl.includes('contrato')) return 'contrato';
  if (sub.includes('mou') || lbl.includes('mou')) return 'mou';
  return sub || lbl;
};

export function useTratosConsolidatedData({ node, nodesSource, edgesSource, allDocs, formData, documents, resolvedNodeSub }) {
  const currentItems = useMemo(() => {
    const items = [];
    Object.entries(formData || {}).forEach(([key, val]) => {
      if (isDisplayableValue(val) && !CONTROL_KEYS.includes(key)) {
        items.push({
          campo: getFieldFriendlyName(key),
          dato: formatValue(val),
          element: getElementTag(resolvedNodeSub),
        });
      }
    });
    (documents || []).forEach(doc => {
      if (doc.url) {
        items.push({
          campo: getDocFriendlyName(doc.type),
          dato: doc.url,
          isLink: true,
          element: getElementTag(resolvedNodeSub),
        });
      }
    });
    items.sort((a, b) => {
      const elemCompare = a.element.localeCompare(b.element);
      if (elemCompare !== 0) return elemCompare;
      return a.campo.localeCompare(b.campo);
    });
    return items;
  }, [formData, documents, resolvedNodeSub]);

  // `document_type`s de todos los nodos que están upstream del nodo actual -- base común de
  // `receivedItems` y `consolidatedJson` (antes se recalculaba en cada uno por separado).
  const upstreamTypes = useMemo(() => {
    if (!node) return [];
    const upstreamIds = getUpstreamNodeIds(node.id, edgesSource || []);
    if (upstreamIds.length === 0) return [];
    return (nodesSource || [])
      .filter(n => upstreamIds.includes(n.id))
      .map(nodeToDocType)
      .filter(Boolean);
  }, [node, edgesSource, nodesSource]);

  const receivedItems = useMemo(() => {
    const items = [];
    if (upstreamTypes.length === 0) return items;

    (allDocs || []).forEach(doc => {
      const docType = (doc.document_type || '').toLowerCase().trim();
      if (!upstreamTypes.includes(docType)) return;
      const tag = getElementTag(doc.document_type);
      Object.entries(doc.form_data || {}).forEach(([key, val]) => {
        if (isDisplayableValue(val) && !CONTROL_KEYS.includes(key)) {
          const campo = getFieldFriendlyName(key);
          if (!items.some(item => item.element === tag && item.campo === campo)) {
            items.push({ campo, dato: formatValue(val), element: tag });
          }
        }
      });
      (doc.documents || []).forEach(f => {
        if (!f.url) return;
        const campo = getDocFriendlyName(f.type);
        if (!items.some(item => item.element === tag && item.campo === campo)) {
          items.push({ campo, dato: f.url, isLink: true, element: tag });
        }
      });
    });

    items.sort((a, b) => {
      const elemCompare = a.element.localeCompare(b.element);
      if (elemCompare !== 0) return elemCompare;
      return a.campo.localeCompare(b.campo);
    });
    return items;
  }, [upstreamTypes, allDocs]);

  const availableVariables = useMemo(() => {
    const unique = [];
    receivedItems.forEach(item => {
      if (!unique.some(x => x.element === item.element && x.campo === item.campo)) {
        unique.push(item);
      }
    });
    return unique;
  }, [receivedItems]);

  const consolidatedJson = useMemo(() => {
    const current = {};
    const currentFiles = {};
    Object.entries(formData || {}).forEach(([key, val]) => {
      if (isDisplayableValue(val) && !CONTROL_KEYS.includes(key)) current[key] = val;
    });
    (documents || []).forEach(doc => {
      if (doc.url) currentFiles[doc.type] = doc.url;
    });

    const received = {};
    (allDocs || []).forEach(doc => {
      const docType = (doc.document_type || '').toLowerCase().trim();
      if (!upstreamTypes.includes(docType)) return;
      const tag = getElementTag(doc.document_type);
      if (!received[tag]) received[tag] = {};
      Object.entries(doc.form_data || {}).forEach(([key, val]) => {
        if (isDisplayableValue(val) && !CONTROL_KEYS.includes(key)) received[tag][key] = val;
      });
      (doc.documents || []).forEach(f => {
        if (f.url) {
          if (!received[tag].files) received[tag].files = {};
          received[tag].files[f.type] = f.url;
        }
      });
    });

    return {
      datos_actuales: {
        elemento: getElementTag(resolvedNodeSub),
        datos: current,
        archivos: currentFiles,
      },
      datos_recibidos: received,
    };
  }, [formData, documents, resolvedNodeSub, upstreamTypes, allDocs]);

  return { currentItems, receivedItems, availableVariables, consolidatedJson };
}
